import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { colors } from '../baseStyle/Variables';
import { ReactComponent as LanguageTogglerIcon } from '../../assets/language-toggler.svg';
import { getCurrentPageName } from '../../state-machine/actions/segment';
import { setCustomerLanguage, SupportedLanguage } from '../../helpers/language/language.helper';
import languageService from '../../services/language.service';

const LanguageToggleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LanguageToggleWrapper = styled.div`
  font-size: 0.75rem;
  line-height: 15px;
  font-weight: bold;
  margin-left: 5px;
`;

const LanguageOption = styled.span<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? 'inherit' : colors.koalafiBlue)};
  text-decoration: ${(props) => (props.isActive ? 'none' : 'underline')};
  cursor: ${(props) => (props.isActive ? 'default' : 'pointer')};
  transition: color 0.2s ease;
`;

const LanguageSeparator = styled.span`
  margin: 0 2px;
`;

interface LanguageToggleProps {
  track: (eventName: string, payload?: object) => void;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({ track }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage;

  const handleLanguageChange = (locale: SupportedLanguage) => {
    if (currentLanguage === locale) return;
    const pageName = getCurrentPageName();
    track('Language Toggled', {
      from: currentLanguage,
      to: locale,
      pageName,
      sourceWindowPath: window.location.pathname,
    });
    // Goes through the helper rather than writing localStorage and i18next directly:
    // an explicit choice is also what makes the language attributable, which decides
    // whether it is reported downstream at all (see getCustomerLanguage).
    setCustomerLanguage(locale);
    // Reported from here rather than from setCustomerLanguage because the Apollo client
    // imports the language helper, so the helper calling a service would be an import cycle.
    languageService.reportLanguageToggled(currentLanguage, locale, pageName);
  };

  return (
    <LanguageToggleContainer>
      <LanguageTogglerIcon />
      <LanguageToggleWrapper>
        <LanguageOption isActive={currentLanguage === 'en'} onClick={() => handleLanguageChange('en')}>
          English
        </LanguageOption>
        <LanguageSeparator>/</LanguageSeparator>
        <LanguageOption isActive={currentLanguage === 'es'} onClick={() => handleLanguageChange('es')}>
          Español
        </LanguageOption>
      </LanguageToggleWrapper>
    </LanguageToggleContainer>
  );
};

export default LanguageToggle;
